import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XMarkIcon,
  DocumentCheckIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';
import PermissionButton from '../components/PermissionButton';
import { 
  useGetContractsQuery,
  useGetActiveContractsQuery,
  useAddContractMutation,
  useUpdateContractMutation,
  useDeleteContractMutation,
  useGetActiveStudentsQuery,
  useGetStudentApplicationsQuery,
  useGetConsultantsQuery,
  useGetActiveUniversitiesQuery,
  useGetActiveProgramsQuery,
  useGetServicesQuery,
  useGetServiceTypesQuery,
  useGetActiveServiceTypesQuery,
  useAddServiceTypeMutation,
  useUpdateServiceTypeMutation,
  useDeleteServiceTypeMutation,
  useGetContractStatusesQuery,
  useGetPaymentStatusesQuery,
  useGetEducationLevelsQuery
} from '../store/api';
import { 
  contractStatuses, 
  paymentStatuses
} from '../data/mockData';
import Pagination from '../components/Pagination';
import ExportButton from '../components/ExportButton';
import { toast } from 'react-toastify';
import { Audio } from 'react-loader-spinner';
import { usePermission } from '../hooks/useAuth';

export default function Contracts() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [consultantFilter, setConsultantFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [viewingContract, setViewingContract] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [isExport, setIsExport] = useState(0);
  
  // Permission hooks - strictly permission-based
  const canAddContract = usePermission('contract.add');
  const canEditContract = usePermission('contract.edit');
  const canDeleteContract = usePermission('contract.delete');
  const canViewContract = usePermission('contract.view'); // Already being used in route protection
  
  // Service Types Modal State
  const [isServiceTypeModalOpen, setIsServiceTypeModalOpen] = useState(false);
  const [editingServiceType, setEditingServiceType] = useState(null);
  const [serviceTypeFormData, setServiceTypeFormData] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'AZN',
    is_active: true
  });
  
  const itemsPerPage = 10;

  // Helper function to handle validation errors from backend
  const handleValidationErrors = (error) => {
    console.error('API Error:', error);
    
    // Check if it's a validation error with specific field errors
    if (error.data && error.data.errors && typeof error.data.errors === 'object') {
      const errors = error.data.errors;
      
      // Show each validation error as a separate toast
      Object.keys(errors).forEach(field => {
        const fieldErrors = errors[field];
        if (Array.isArray(fieldErrors)) {
          fieldErrors.forEach(errorMessage => {
            // Map field names to user-friendly names
            const fieldNames = {
              'start_date': 'Başlama tarixi',
              'end_date': 'Bitmə tarixi',
              'contract_date': 'Müqavilə tarixi',
              'student_id': 'Tələbə',
              'consultant_id': 'Konsultant',
              'university_id': 'Universitet',
              'program_id': 'Proqram',
              'total_amount': 'Ümumi məbləğ',
              'payment_type': 'Ödəniş növü',
              'status_id': 'Status',
              'services': 'Xidmətlər',
              'payment_schedule': 'Ödəniş cədvəli'
            };
            
            const friendlyFieldName = fieldNames[field] || field;
            const friendlyMessage = errorMessage
              .replace('The start date field must be a date after or equal to contract date.', 
                      'Başlama tarixi müqavilə tarixindən sonra və ya bərabər olmalıdır.')
              .replace('The end date field must be a date after start date.', 
                      'Bitmə tarixi başlama tarixindən sonra olmalıdır.')
              .replace('The student id field is required.', 
                      'Tələbə seçimi məcburidir.')
              .replace('The consultant id field is required.', 
                      'Konsultant seçimi məcburidir.')
              .replace('The university id field is required.', 
                      'Universitet seçimi məcburidir.')
              .replace('The program id field is required.', 
                      'Proqram seçimi məcburidir.')
              .replace('The total amount field must be greater than 0.', 
                      'Ümumi məbləğ 0-dan böyük olmalıdır.')
              .replace('The services field is required.', 
                      'Ən azı bir xidmət seçilməlidir.')
              .replace('The contract date field is required.', 
                      'Müqavilə tarixi məcburidir.')
              .replace('The contract date field must be a valid date.', 
                      'Müqavilə tarixi düzgün formatda olmalıdır.')
              .replace('The start date field is required.', 
                      'Başlama tarixi məcburidir.')
              .replace('The start date field must be a valid date.', 
                      'Başlama tarixi düzgün formatda olmalıdır.')
              .replace('The end date field is required.', 
                      'Bitmə tarixi məcburidir.')
              .replace('The end date field must be a valid date.', 
                      'Bitmə tarixi düzgün formatda olmalıdır.')
              .replace('The payment type field is required.', 
                      'Ödəniş növü seçimi məcburidir.')
              .replace('The status id field is required.', 
                      'Status seçimi məcburidir.')
              .replace('The original total amount field must be greater than 0.', 
                      'Orijinal məbləğ 0-dan böyük olmalıdır.')
              .replace('The discounted amount field must be greater than 0.', 
                      'Endirimli məbləğ 0-dan böyük olmalıdır.')
              .replace('The discounted amount must not be greater than original total amount.', 
                      'Endirimli məbləğ orijinal məbləğdən böyük ola bilməz.')
              .replace('The selected student is required.', 
                      'Tələbə seçimi məcburidir.')
              .replace('The selected consultant is required.', 
                      'Konsultant seçimi məcburidir.')
              .replace('The selected university is required.', 
                      'Universitet seçimi məcburidir.')
              .replace('The selected program is required.', 
                      'Proqram seçimi məcburidir.')
              .replace('Validation failed', 
                      'Məlumat yoxlanışı uğursuz oldu')
              .replace('The given data was invalid.', 
                      'Daxil edilən məlumatlar səhvdir.');
            
            toast.error(`${friendlyFieldName}: ${friendlyMessage}`, {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            });
          });
        }
      });
    } else if (error.data && error.data.message) {
      // Single error message
      toast.error(error.data.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } else {
      // Generic error
      toast.error('Gözlənilməz xəta baş verdi. Zəhmət olmasa yenidən cəhd edin.', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  // API hooks
  const { data: contractsData, isLoading: contractsLoading, error: contractsError, refetch: refetchContracts } = useGetContractsQuery();
  const { data: studentsData, isLoading: studentsLoading } = useGetActiveStudentsQuery();
  const { data: applicationsData, isLoading: applicationsLoading } = useGetStudentApplicationsQuery();
  const { data: consultantsData, isLoading: consultantsLoading } = useGetConsultantsQuery();
  const { data: universitiesData, isLoading: universitiesLoading } = useGetActiveUniversitiesQuery();
  const { data: programsData, isLoading: programsLoading } = useGetActiveProgramsQuery();
  const { data: servicesData, isLoading: servicesLoading } = useGetServicesQuery();
  const { data: serviceTypesData, isLoading: serviceTypesLoading } = useGetServiceTypesQuery();
  const { data: contractStatusesData, isLoading: contractStatusesLoading } = useGetContractStatusesQuery();
  const { data: paymentStatusesData, isLoading: paymentStatusesLoading } = useGetPaymentStatusesQuery();
  const { data: educationLevelsData } = useGetEducationLevelsQuery();
  
  // Service Types API hooks
  const { data: allServiceTypesData, isLoading: allServiceTypesLoading, refetch: refetchServiceTypes } = useGetServiceTypesQuery({
    keyword: '',
    page: 1,
    limit: 50
  });
  
  const [addContract, { isLoading: addLoading }] = useAddContractMutation();
  const [updateContract, { isLoading: updateLoading }] = useUpdateContractMutation();
  const [deleteContract, { isLoading: deleteLoading }] = useDeleteContractMutation();
  
  // Service Type mutations
  const [addServiceType, { isLoading: addServiceTypeLoading }] = useAddServiceTypeMutation();
  const [updateServiceType, { isLoading: updateServiceTypeLoading }] = useUpdateServiceTypeMutation();
  const [deleteServiceType, { isLoading: deleteServiceTypeLoading }] = useDeleteServiceTypeMutation();

  // Extract data from API responses and use real payment schedules
  const contracts = (contractsData?.data || []).map(contract => ({
    ...contract,
    // Use real payment schedule from backend or initialize empty if not available
    paymentSchedule: contract.payment_schedule || [],
    // Ensure backward compatibility
    payment_schedule: contract.payment_schedule || []
  }));
  const students = studentsData?.data || [];
  const consultants = consultantsData?.data || [];
  const universities = universitiesData?.data || [];
  const universityPrograms = programsData?.data || [];
  const services = servicesData?.data || [];
  const serviceTypes = serviceTypesData?.data || [];
  const educationLevels = educationLevelsData?.data || educationLevelsData || [];

  // Helper function to get degree names from IDs
  const getDegreeNames = (degreeIds) => {
    if (!degreeIds || !educationLevels) return '';
    
    const ids = typeof degreeIds === 'string' ? 
      degreeIds.split(',').map(id => id.trim()) : 
      degreeIds;
    
    const names = ids.map(id => {
      const level = educationLevels.find(l => l.id && l.id.toString() === id.toString());
      return level ? level.name : id;
    }).filter(Boolean);
    
    return names.join(', ');
  };
  
  // Use backend contract statuses with fallback to mock data
  const contractStatusesFromAPI = contractStatusesData?.data || [];
  const contractStatusesAvailable = contractStatusesFromAPI.length > 0 ? contractStatusesFromAPI : contractStatuses;
  
  // Use backend payment statuses with fallback to mock data
  const paymentStatusesFromAPI = paymentStatusesData?.data || [];
  const paymentStatusesAvailable = paymentStatusesFromAPI.length > 0 ? paymentStatusesFromAPI : paymentStatuses;

  const [formData, setFormData] = useState({
    student_id: '',
    application_id: '',
    consultant_id: '',
    university_id: '',
    program_id: '',
    services: [],
    original_total_amount: 0, // Xidmətlərin real cəmi
    discounted_amount: 0, // Endirimli qiymət  
    total_amount: 0, // Final amount (discounted_amount və ya original_total_amount)
    payment_type: 'Tam',
    payment_installments: 2,
    payment_dates: [],
    contract_date: new Date().toISOString().split('T')[0],
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    status_id: '',
    notes: ''
  });

  const [paymentData, setPaymentData] = useState({
    amount: '',
    paymentMethod: 'Bank köçürməsi',
    reference: '',
    notes: ''
  });

  // Filter contracts
  const filteredContracts = contracts.filter(contract => {
    const contractNumber = contract.contract_number || contract.contractNumber || '';
    const studentName = contract.student 
      ? `${contract.student.first_name || ''} ${contract.student.last_name || ''}`.trim()
      : (contract.studentName || contract.student_name || '');
    const universityName = contract.university?.name || contract.universityName || contract.university_name || '';
    
    const matchesSearch = 
      contractNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      universityName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const statusId = contract.status_id || contract.statusId || '';
    const consultantId = contract.consultant_id || contract.consultantId || '';
    
    const matchesStatus = !statusFilter || statusId.toString() === statusFilter;
    const matchesPayment = !paymentFilter || getContractPaymentStatus(contract).toString() === paymentFilter;
    const matchesConsultant = !consultantFilter || consultantId.toString() === consultantFilter;
    
    return matchesSearch && matchesStatus && matchesPayment && matchesConsultant;
  });

  // Pagination
  const totalPages = Math.ceil(filteredContracts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedContracts = filteredContracts.slice(startIndex, startIndex + itemsPerPage);

  // Get contract payment status
  function getContractPaymentStatus(contract) {
    const paymentSchedule = contract.paymentSchedule || contract.payment_schedule || [];
    const totalPaid = paymentSchedule.reduce((sum, payment) => {
      const paidAmount = parseFloat(payment.paidAmount || payment.paid_amount || 0);
      return sum + (isNaN(paidAmount) ? 0 : paidAmount);
    }, 0);
    const totalAmount = parseFloat(contract.totalAmount || contract.total_amount || 0);
    
    if (totalPaid === 0) return 1; // Gözlənilir
    if (totalPaid < totalAmount) return 2; // Qismən ödənilib
    if (totalPaid >= totalAmount) return 3; // Tam ödənilib
    
    return 1;
  }

  // Check if contract has any paid payments (to disable editing)
  function hasAnyPaidPayments(contract) {
    const paymentSchedule = contract.paymentSchedule || contract.payment_schedule || [];
    return paymentSchedule.some(payment => {
      const statusId = payment.statusId || payment.status_id;
      return parseInt(statusId) === 2; // 2 = "Ödənilib" (Paid)
    });
  }

  // Get overdue payments
  function getOverduePayments() {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
    const overdue = [];
    
    contracts.forEach(contract => {
      const paymentSchedule = contract.paymentSchedule || contract.payment_schedule || [];
      paymentSchedule.forEach(payment => {
        const statusId = payment.statusId || payment.status_id;
        const dueDate = new Date(payment.dueDate || payment.due_date);
        dueDate.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
        
        // Check if payment is overdue: not paid (status !== 2) and due date has passed
        if (parseInt(statusId) !== 2 && dueDate < today) {
          overdue.push({
            ...payment,
            contractNumber: contract.contract_number || contract.contractNumber,
            studentName: contract.student 
              ? `${contract.student.first_name || ''} ${contract.student.last_name || ''}`.trim()
              : (contract.studentName || contract.student_name),
            daysOverdue: Math.floor((today - dueDate) / (1000 * 60 * 60 * 24))
          });
        }
      });
    });
    
    return overdue;
  }

  // Get upcoming payments (next 7 days)
  function getUpcomingPayments() {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    const upcoming = [];
    
    contracts.forEach(contract => {
      const paymentSchedule = contract.paymentSchedule || contract.payment_schedule || [];
      paymentSchedule.forEach(payment => {
        const statusId = payment.statusId || payment.status_id;
        const dueDate = new Date(payment.dueDate || payment.due_date);
        dueDate.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
        
        // Check if payment is upcoming: not paid (status !== 2) and due within next 7 days
        if (parseInt(statusId) !== 2 && dueDate >= today && dueDate <= nextWeek) {
          upcoming.push({
            ...payment,
            contractNumber: contract.contract_number || contract.contractNumber,
            studentName: contract.student 
              ? `${contract.student.first_name || ''} ${contract.student.last_name || ''}`.trim()
              : (contract.studentName || contract.student_name),
            daysUntilDue: Math.floor((dueDate - today) / (1000 * 60 * 60 * 24))
          });
        }
      });
    });
    
    return upcoming;
  }

  // State to track which student application this contract is for
  const [selectedStudentApplication, setSelectedStudentApplication] = useState(null);

  // Helper function to map backend payment type to UI display
  const mapPaymentTypeToUI = (backendPaymentType) => {
    if (backendPaymentType === 'full') return 'Tam';
    if (backendPaymentType === 'partial') return 'Hissəli';
    if (backendPaymentType === 'Tam' || backendPaymentType === 'Hissəli') return backendPaymentType;
    return 'Naməlum';
  };

  const handleAdd = (studentApplication = null) => {
    setEditingContract(null);
    setSelectedStudentApplication(studentApplication);
    
    // If creating from student application, pre-populate the form
    if (studentApplication) {
        const student = students.find(s => s.id === studentApplication.studentId);
        const university = universities.find(u => u.id === studentApplication.universityId);
        const program = universityPrograms.find(p => p.id === studentApplication.programId);
        
        // Safe name extraction
        const universityName = typeof university?.name === 'object' 
          ? university.name?.name || 'Universitet'
          : university?.name || 'Universitet';
        const programName = typeof program?.name === 'object' 
          ? program.name?.name || 'Proqram'
          : program?.name || 'Proqram';
        
        setFormData({
          student_id: studentApplication.studentId.toString(),
          application_id: studentApplication.applicationId ? studentApplication.applicationId.toString() : '',
          consultant_id: student ? (student.consultantId || student.consultant_id || '').toString() : '',
          university_id: studentApplication.universityId.toString(),
          program_id: studentApplication.programId.toString(),
          services: [],
          original_total_amount: 0,
          discounted_amount: 0,
          total_amount: 0,
          payment_type: 'Hissəli',
          payment_installments: 2,
          payment_dates: ['', ''],
          contract_date: new Date().toISOString().split('T')[0],
          start_date: new Date().toISOString().split('T')[0],
          end_date: '',
          status_id: '',
          notes: studentApplication.applicationId 
            ? `${universityName} - ${programName} proqramı üçün müqavilə (Müraciət #${studentApplication.applicationId})`
            : `${universityName} - ${programName} proqramı üçün müqavilə`
        });
    } else {
      setFormData({
        student_id: '',
        application_id: '',
        consultant_id: '',
        university_id: '',
        program_id: '',
        services: [],
        original_total_amount: 0,
        discounted_amount: 0,
        total_amount: 0,
        payment_type: 'Tam',
        payment_installments: 2,
        payment_dates: [],
        contract_date: new Date().toISOString().split('T')[0],
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        status_id: '',
        notes: ''
      });
    }
    
    setIsModalOpen(true);
  };

  const handleEdit = (contract) => {
    setEditingContract(contract);
    
    // Extract payment schedule info for installments
    const paymentSchedule = contract.paymentSchedule || contract.payment_schedule || [];
    const installmentCount = paymentSchedule.length || 2;
    
    console.log('Edit Contract Debug:', {
      contract,
      paymentSchedule,
      installmentCount,
      services: contract.services
    });
    
    // Convert backend date format to HTML date input format
    const paymentDates = paymentSchedule.map(p => {
      const dueDate = p.dueDate || p.due_date || '';
      if (dueDate) {
        // Convert "2025-07-15T00:00:00.000000Z" to "2025-07-15"
        const convertedDate = dueDate.split('T')[0];
        console.log('Converting date:', dueDate, '→', convertedDate);
        return convertedDate;
      }
      return '';
    });
    
    // Convert contract dates to proper format
    const convertDate = (dateString) => {
      if (!dateString) return '';
      return dateString.split('T')[0];
    };
    
    // Determine payment type based on installment count and map backend values
    const backendPaymentType = contract.paymentType || contract.payment_type;
    let paymentType;
    
    // Map backend English values to Azerbaijani for UI
    if (backendPaymentType === 'full') {
      paymentType = 'Tam';
    } else if (backendPaymentType === 'partial') {
      paymentType = 'Hissəli';
    } else if (backendPaymentType === 'Tam' || backendPaymentType === 'Hissəli') {
      // Backward compatibility for old Azerbaijani values
      paymentType = backendPaymentType;
    } else {
      // Fallback based on installment count
      paymentType = installmentCount > 1 ? 'Hissəli' : 'Tam';
    }
    
    // Ensure payment_dates array has correct length for installments
    const adjustedPaymentDates = [...paymentDates];
    while (adjustedPaymentDates.length < installmentCount) {
      adjustedPaymentDates.push('');
    }
    
    console.log('Payment dates mapping:', {
      originalPaymentDates: paymentDates,
      adjustedPaymentDates,
      paymentType,
      installmentCount
    });
    
    console.log('Payment type mapping:', {
      backendPaymentType,
      mappedPaymentType: paymentType
    });
    
    // Debug services mapping
    const mappedServices = (contract.services || []).map(service => ({
      service_id: service.service_type_id || service.service_id || service.id,
      service_name: service.service_type?.name || service.service_name || service.name,
      price: parseFloat(service.price) || 0,
      currency: service.currency || 'AZN'
    }));
    
    console.log('Services mapping:', {
      originalServices: contract.services,
      mappedServices
    });
    
    console.log('Contract statuses debug:', {
      contractStatusesFromAPI,
      contractStatusesAvailable,
      contractStatusesLoading
    });
    
    console.log('Payment statuses debug:', {
      paymentStatusesFromAPI,
      paymentStatusesAvailable,
      paymentStatusesLoading
    });
    
    setFormData({
      student_id: (contract.studentId || contract.student_id || '').toString(),
      application_id: (contract.applicationId || contract.application_id || '').toString(),
      consultant_id: (contract.consultantId || contract.consultant_id || '').toString(),
      university_id: (contract.universityId || contract.university_id || '').toString(),
      program_id: (contract.programId || contract.program_id || '').toString(),
      services: mappedServices,
      original_total_amount: parseFloat(contract.original_total_amount) || parseFloat(contract.totalAmount || contract.total_amount) || 0,
      discounted_amount: parseFloat(contract.discounted_amount) || parseFloat(contract.totalAmount || contract.total_amount) || 0,
      total_amount: parseFloat(contract.totalAmount || contract.total_amount) || 0,
      payment_type: paymentType,
      payment_installments: installmentCount,
      payment_dates: adjustedPaymentDates,
      contract_date: convertDate(contract.contractDate || contract.contract_date),
      start_date: convertDate(contract.startDate || contract.start_date),
      end_date: convertDate(contract.endDate || contract.end_date),
      status_id: (contract.statusId || contract.status_id || '1').toString(),
      notes: contract.notes || ''
    });
    setIsModalOpen(true);
  };

  const handleView = (contract) => {
    setViewingContract(contract);
    setActiveTab('details');
    setIsViewModalOpen(true);
  };

  const handlePayment = (contract, payment) => {
    setViewingContract(contract);
    setSelectedPayment(payment);
    setPaymentData({
      amount: payment.amount - (payment.paid_amount || payment.paidAmount),
      paymentMethod: 'Bank köçürməsi',
      reference: '',
      notes: ''
    });
    setIsPaymentModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.student_id || !formData.consultant_id || !formData.university_id || !formData.program_id || !formData.status_id) {
      toast.error('Zəhmət olmasa bütün məcburi sahələri doldurun!', {
        position: "top-right",
        autoClose: 4000,
      });
      return;
    }
    
    if (formData.services.length === 0) {
      toast.error('Ən azı bir xidmət seçin!', {
        position: "top-right",
        autoClose: 4000,
      });
      return;
    }
    
    if (!formData.end_date || new Date(formData.end_date) <= new Date(formData.start_date)) {
      toast.error('Bitmə tarixi başlama tarixindən sonra olmalıdır!', {
        position: "top-right",
        autoClose: 4000,
      });
      return;
    }
    
    // Validate installment payment dates
    if (formData.payment_type === 'Hissəli') {
      const emptyDates = formData.payment_dates.filter(date => !date || date.trim() === '');
      if (emptyDates.length > 0) {
        toast.error('Hissəli ödəniş üçün bütün ödəniş tarixlərini təyin edin!', {
          position: "top-right",
          autoClose: 4000,
        });
        return;
      }
      
      // Check if dates are in chronological order
      const sortedDates = [...formData.payment_dates].sort();
      const areDatesInOrder = formData.payment_dates.every((date, index) => 
        index === 0 || new Date(date) >= new Date(formData.payment_dates[index - 1])
      );
      
      if (!areDatesInOrder) {
        toast.error('Ödəniş tarixləri ardıcıl olmalıdır!', {
          position: "top-right",
          autoClose: 4000,
        });
        return;
      }
    }
    
    // Status change validation
    if (editingContract && formData.status_id !== (editingContract.status_id || editingContract.statusId || '').toString()) {
      const oldStatusName = contractStatusesAvailable.find(s => s.id === (editingContract.status_id || editingContract.statusId))?.name;
      const newStatusName = contractStatusesAvailable.find(s => s.id.toString() === formData.status_id)?.name;
      
      // Special validation for certain status changes
      if (formData.status_id === '3' || formData.status_id === '4') { // İmzalandı or Aktiv
        const paymentSchedule = editingContract.paymentSchedule || editingContract.payment_schedule || [];
        const hasUnpaidPayments = paymentSchedule.some(p => (p.statusId || p.status_id) !== 3);
        if (hasUnpaidPayments && !window.confirm(`Müqaviləni "${newStatusName}" statusuna keçirmək istədiyinizə əminsiniz?\n\nQeyd: Hələ ödənilməmiş ödənişlər mövcuddur.`)) {
          return;
        }
      }
      
      if (formData.status_id === '5') { // Tamamlandı
        if (!window.confirm(`Müqaviləni tamamlandı olaraq işarələmək istədiyinizə əminsiniz?\n\nBu əməliyyat geri alına bilməz.`)) {
          return;
        }
      }
      
      if (formData.status_id === '6') { // Ləğv edildi
        if (!window.confirm(`Müqaviləni ləğv etmək istədiyinizə əminsiniz?\n\nBu əməliyyat ciddi nəticələrə səbəb ola bilər.`)) {
          return;
        }
      }
    }
    
    // Generate payment schedule based on payment type
    const generatePaymentSchedule = () => {
      const schedule = [];
      const totalAmount = parseFloat(formData.total_amount) || 0; // Bu endirimli qiymətdir
      
      // Determine installment count and dates
      let installments, paymentDates;
      
      if (formData.payment_type === 'Tam') {
        installments = 1;
        // Single payment due in 15 days if no custom date specified
        const defaultDate = new Date(formData.start_date);
        defaultDate.setDate(defaultDate.getDate() + 15);
        paymentDates = [defaultDate.toISOString().split('T')[0]];
      } else {
        installments = parseInt(formData.payment_installments) || 2;
        paymentDates = formData.payment_dates.length > 0 ? formData.payment_dates : [];
        
        // Generate default dates if not specified
        if (paymentDates.length < installments) {
          paymentDates = [];
          for (let i = 0; i < installments; i++) {
            const defaultDate = new Date(formData.start_date);
            defaultDate.setDate(defaultDate.getDate() + (i + 1) * 15);
            paymentDates.push(defaultDate.toISOString().split('T')[0]);
          }
        }
      }
      
      // Generate schedule for all payment types using same logic
      for (let i = 0; i < installments; i++) {
        const isLastPayment = i === installments - 1;
        const amount = isLastPayment 
          ? totalAmount - (schedule.reduce((sum, p) => sum + parseFloat(p.amount), 0))
          : Math.round((totalAmount / installments) * 100) / 100; // Round to 2 decimal places
        
        const dueDate = paymentDates[i] || (() => {
          const defaultDate = new Date(formData.start_date);
          defaultDate.setDate(defaultDate.getDate() + (i + 1) * 15);
          return defaultDate.toISOString().split('T')[0];
        })();
        
        const description = installments === 1 
          ? 'Tam ödəniş'
          : `${i + 1}. ödəniş (${Math.round((amount / totalAmount) * 100)}%)`;
        
        schedule.push({
          id: i + 1,
          amount: amount,
          currency: 'AZN',
          due_date: dueDate,
          description: description,
          status_id: 1,
          status: 'Gözlənilir',
          paid_date: null,
          paid_amount: 0
        });
      }
      
      return schedule;
    };

    try {
      // Map UI payment type to backend format
      const mapPaymentTypeToBackend = (uiPaymentType) => {
        if (uiPaymentType === 'Tam') return 'full';
        if (uiPaymentType === 'Hissəli') return 'partial';
        return uiPaymentType; // fallback
      };

      console.log('Form submission payment type mapping:', {
        uiPaymentType: formData.payment_type,
        backendPaymentType: mapPaymentTypeToBackend(formData.payment_type)
      });

      // Determine if payment schedule should be sent
      const backendPaymentType = mapPaymentTypeToBackend(formData.payment_type);
      const shouldSendPaymentSchedule = backendPaymentType === 'partial';
      
      // Create contract object for API
      const contractData = {
        student_id: parseInt(formData.student_id),
        application_id: formData.application_id ? parseInt(formData.application_id) : null,
        consultant_id: parseInt(formData.consultant_id),
        university_id: parseInt(formData.university_id),
        program_id: parseInt(formData.program_id),
        services: formData.services.map(service => ({
          service_type_id: service.service_id,
          price: parseFloat(service.price),
          quantity: 1
        })),
        original_total_amount: parseFloat(formData.original_total_amount), // Xidmətlərin real cəmi
        discounted_amount: parseFloat(formData.discounted_amount), // Endirimli qiymət
        total_amount: parseFloat(formData.total_amount), // Final amount (əsasən discounted_amount ilə eyni)
        payment_type: backendPaymentType,
        contract_date: formData.contract_date,
        start_date: formData.start_date,
        end_date: formData.end_date,
        status_id: parseInt(formData.status_id),
        notes: formData.notes || ''
      };

      // Only add payment_schedule for partial payments
      if (shouldSendPaymentSchedule) {
        if (editingContract && editingContract.paymentSchedule && editingContract.paymentSchedule.length > 0) {
          // Use existing payment schedule for edit, but regenerate if payment type changed from full to partial
          const originalBackendPaymentType = editingContract.payment_type || editingContract.paymentType;
          const wasFullPayment = originalBackendPaymentType === 'full' || originalBackendPaymentType === 'Tam';
          
          if (wasFullPayment && backendPaymentType === 'partial') {
            // Generate new payment schedule when changing from full to partial
            contractData.payment_schedule = generatePaymentSchedule();
          } else {
            // Keep existing payment schedule
            contractData.payment_schedule = editingContract.paymentSchedule || editingContract.payment_schedule;
          }
        } else {
          // Generate new payment schedule for new contracts or when none exists
          contractData.payment_schedule = generatePaymentSchedule();
        }
      }
      
      console.log('Contract data for submission:', {
        paymentType: formData.payment_type,
        backendPaymentType,
        shouldSendPaymentSchedule,
        hasPaymentSchedule: !!contractData.payment_schedule,
        contractData
      });

      let response;
      if (editingContract) {
        response = await updateContract({ id: editingContract.id, obj: contractData });
      } else {
        response = await addContract(contractData);
      }

      if (response.error) {
        handleValidationErrors(response.error);
        return;
      }

      // Refetch contracts to update statistics
      refetchContracts();

      // Show success message
      const action = editingContract ? 'yeniləndi' : 'yaradıldı';
      const student = students.find(s => s.id.toString() === formData.student_id);
      const firstName = student?.first_name || student?.firstName || 'Ad';
      const lastName = student?.last_name || student?.lastName || 'Soyad';
      const studentName = student ? `${firstName} ${lastName}` : 'Naməlum';
      const contractNumber = response.data?.contract_number || response.data?.contractNumber || `UNI-2024-${String(Date.now()).slice(-3)}`;
      
      toast.success(`Müqavilə uğurla ${action}! 
Müqavilə №: ${contractNumber}
Tələbə: ${studentName}
Ümumi məbləğ: ${contractData.total_amount} AZN`, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      setIsModalOpen(false);
      setSelectedStudentApplication(null);
    } catch (error) {
      console.error('Contract operation error:', error);
      handleValidationErrors(error);
    }
  };

  const handleDeleteContract = async (contract) => {
    const contractNumber = contract.contract_number || contract.contractNumber || 'Naməlum';
    const studentName = contract.student 
      ? `${contract.student.first_name || ''} ${contract.student.last_name || ''}`.trim()
      : (contract.studentName || contract.student_name || 'Naməlum');
    
    if (window.confirm(`"${contractNumber}" nömrəli müqaviləni silmək istədiyinizə əminsiniz?\n\nTələbə: ${studentName}\n\nBu əməliyyat geri alına bilməz!`)) {
      try {
        const response = await deleteContract(contract.id);
        if (response.error) {
          handleValidationErrors(response.error);
        } else {
          toast.success('Müqavilə uğurla silindi!', {
            position: "top-right",
            autoClose: 3000,
          });
        }
      } catch (error) {
        console.error('Delete contract error:', error);
        handleValidationErrors(error);
      }
    }
  };

  const handlePaymentSubmit = (e) => {
    e.preventDefault();
    console.log('Payment data:', {
      contract: viewingContract,
      payment: selectedPayment,
      paymentData
    });
    alert(`${paymentData.amount} AZN ödəniş qeydə alındı!`);
    setIsPaymentModalOpen(false);
  };

  const getStatusBadge = (statusId, statusName) => {
    const status = contractStatusesAvailable.find(s => s.id === statusId);
    const colorClass = {
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      green: 'bg-green-100 text-green-800 border-green-200',
      gray: 'bg-gray-100 text-gray-800 border-gray-200',
      red: 'bg-red-100 text-red-800 border-red-200'
    }[status?.color] || 'bg-gray-100 text-gray-800 border-gray-200';

    // Convert statusName to string if it's an object
    const displayName = typeof statusName === 'object' ? statusName?.name || 'Naməlum' : statusName || 'Naməlum';

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${colorClass}`}>
        {displayName}
      </span>
    );
  };

  const getPaymentStatusBadge = (contract) => {
    const statusId = getContractPaymentStatus(contract);
    const status = paymentStatusesAvailable.find(s => s.id === statusId);
    const colorClass = {
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      orange: 'bg-orange-100 text-orange-800 border-orange-200',
      green: 'bg-green-100 text-green-800 border-green-200',
      red: 'bg-red-100 text-red-800 border-red-200',
      gray: 'bg-gray-100 text-gray-800 border-gray-200'
    }[status?.color] || 'bg-gray-100 text-gray-800 border-gray-200';

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${colorClass}`}>
        {status?.name}
      </span>
    );
  };

  // Helper function to check if contract needs attention
  const getContractAttentionLevel = (contract) => {
    // Check for overdue payments
    const paymentSchedule = contract.paymentSchedule || contract.payment_schedule || [];
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
    
    const hasOverduePayments = paymentSchedule.some(payment => {
      const statusId = payment.statusId || payment.status_id;
      const dueDate = new Date(payment.dueDate || payment.due_date);
      dueDate.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
      
      // Payment is overdue if: not paid (status !== 2) and due date has passed
      return parseInt(statusId) !== 2 && dueDate < today;
    });
    
    // Check for status inconsistencies
    const contractStatusId = contract.statusId || contract.status_id;
    const isActiveButUnpaid = (contractStatusId === 4) && paymentSchedule.some(p => {
      const statusId = p.statusId || p.status_id;
      return parseInt(statusId) !== 2;
    });
    
    if (hasOverduePayments) return 'overdue';
    if (isActiveButUnpaid) return 'warning';
    return 'normal';
  };

  const overduePayments = getOverduePayments();
  const upcomingPayments = getUpcomingPayments();

  // Service Type Modal functions
  const handleOpenServiceTypeModal = () => {
    setIsServiceTypeModalOpen(true);
    setEditingServiceType(null);
    setServiceTypeFormData({
      name: '',
      description: '',
      price: '',
      currency: 'AZN',
      is_active: true
    });
  };

  const handleCloseServiceTypeModal = () => {
    setIsServiceTypeModalOpen(false);
    setEditingServiceType(null);
    setServiceTypeFormData({
      name: '',
      description: '',
      price: '',
      currency: 'AZN',
      is_active: true
    });
  };

  const handleEditServiceType = (serviceType) => {
    setEditingServiceType(serviceType);
    setServiceTypeFormData({
      name: serviceType.name,
      description: serviceType.description,
      price: serviceType.price,
      currency: serviceType.currency,
      is_active: serviceType.is_active
    });
  };

  const handleDeleteServiceType = async (serviceTypeId) => {
    if (window.confirm('Bu xidmət növünü silmək istədiyinizə əminsiniz?')) {
      try {
        await deleteServiceType(serviceTypeId).unwrap();
        toast.success('Xidmət növü müvəffəqiyyətlə silindi');
        refetchServiceTypes();
      } catch (error) {
        toast.error('Xidmət növü silinərkən xəta baş verdi');
      }
    }
  };

  const handleServiceTypeSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingServiceType) {
        await updateServiceType({ id: editingServiceType.id, obj: serviceTypeFormData }).unwrap();
        toast.success('Xidmət növü müvəffəqiyyətlə yeniləndi');
      } else {
        await addServiceType(serviceTypeFormData).unwrap();
        toast.success('Xidmət növü müvəffəqiyyətlə əlavə edildi');
      }
      handleCloseServiceTypeModal();
      refetchServiceTypes();
    } catch (error) {
      const errorMessage = error?.data?.message || 'Xəta baş verdi';
      toast.error(errorMessage);
    }
  };

  // Get contract statistics
  const totalContracts = contracts.length;
  
  // Find status IDs dynamically from backend data
  const activeStatusId = contractStatusesAvailable.find(s => 
    s.name === 'Aktiv' || s.name === 'Active'
  )?.id || 4;
  
  const completedStatusId = contractStatusesAvailable.find(s => 
    s.name === 'Tamamlandı' || s.name === 'Tamamlanmış' || s.name === 'Completed'
  )?.id || 5;
  
  const activeContracts = contracts.filter(c => {
    const contractStatusId = c.status_id || c.statusId;
    return parseInt(contractStatusId) === parseInt(activeStatusId);
  }).length;
  
  const completedContracts = contracts.filter(c => {
    const contractStatusId = c.status_id || c.statusId;
    return parseInt(contractStatusId) === parseInt(completedStatusId);
  }).length;
  
  // Debug statistics calculation
  console.log('Contract Statistics Debug:', {
    totalContracts,
    activeStatusId,
    completedStatusId,
    activeContracts,
    completedContracts,
    availableStatuses: contractStatusesAvailable.map(s => ({ id: s.id, name: s.name })),
    contractsStatusBreakdown: contracts.map(c => ({
      id: c.id,
      status_id: c.status_id || c.statusId,
      status_id_type: typeof (c.status_id || c.statusId),
      statusName: contractStatusesAvailable.find(s => parseInt(s.id) === parseInt(c.status_id || c.statusId))?.name,
      matchesCompleted: parseInt(c.status_id || c.statusId) === parseInt(completedStatusId)
    }))
  });
  
  // Calculate total revenue from completed payments
  const totalRevenue = contracts.reduce((sum, contract) => {
    const paymentSchedule = contract.paymentSchedule || contract.payment_schedule || [];
    const paidAmount = paymentSchedule.reduce((paymentSum, payment) => {
      const originalPaid = payment.paidAmount || payment.paid_amount || 0;
      const paid = parseFloat(originalPaid);
      
      console.log('Payment debug:', {
        originalPaid,
        parsed: paid,
        isNaN: isNaN(paid),
        finalValue: isNaN(paid) ? 0 : paid
      });
      
      return paymentSum + (isNaN(paid) ? 0 : paid);
    }, 0);
    return sum + paidAmount;
  }, 0);
  
  console.log('Total Revenue Debug:', {
    contractsCount: contracts.length,
    totalRevenue,
    formattedRevenue: totalRevenue.toLocaleString('az-AZ', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })
  });

  // Show loading state
  if (contractsLoading || studentsLoading || applicationsLoading || consultantsLoading || universitiesLoading || programsLoading || contractStatusesLoading || paymentStatusesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="text-gray-600">Yüklənir...</span>
        </div>
      </div>
    );
  }

  // Show error state
  if (contractsError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 mb-2">
            <ExclamationTriangleIcon className="h-12 w-12 mx-auto" />
          </div>
          <p className="text-gray-600">Müqavilələr yüklənərkən xəta baş verdi</p>
          <p className="text-sm text-gray-500 mt-1">
            {contractsError?.data?.message || 'Bilinməyən xəta'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Müqavilələr</h1>
          <p className="mt-1 text-sm text-gray-600">
            Tələbə müqavilələrini və ödənişləri idarə edin
          </p>
          <div className="mt-2 flex items-center space-x-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
              Ümumi: {contracts.length}
            </span>
            {(searchTerm || statusFilter || paymentFilter || consultantFilter) && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                Tapılan: {filteredContracts.length}
              </span>
            )}
          </div>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <ExportButton 
            data={filteredContracts.map(contract => ({
              contractNumber: contract.contract_number || contract.contractNumber || '',
              studentName: contract.student 
                ? `${contract.student.first_name || ''} ${contract.student.last_name || ''}`.trim()
                : (contract.studentName || contract.student_name || ''),
              consultantName: contract.consultant?.admin 
                ? `${contract.consultant.admin.first_name || ''} ${contract.consultant.admin.last_name || ''}`.trim()
                : (contract.consultantName || contract.consultant_name || ''),
              universityName: contract.university?.name || contract.universityName || contract.university_name || '',
              originalTotalAmount: contract.original_total_amount ? `${parseFloat(contract.original_total_amount).toLocaleString('az-AZ', { minimumFractionDigits: 2 })} AZN` : '',
              totalAmount: `${parseFloat(contract.totalAmount || contract.total_amount || 0).toLocaleString('az-AZ', { minimumFractionDigits: 2 })} AZN`,
              status: contractStatusesAvailable.find(s => parseInt(s.id) === parseInt(contract.status_id || contract.statusId))?.name || 'Naməlum',
              contractDate: contract.contractDate || contract.contract_date ? 
                new Date(contract.contractDate || contract.contract_date).toLocaleDateString('az-AZ') : ''
            }))}
            filename="muqavileler"
            columns={[
              { key: 'contractNumber', label: 'Müqavilə №' },
              { key: 'studentName', label: 'Tələbə' },
              { key: 'consultantName', label: 'Konsultant' },
              { key: 'universityName', label: 'Universitet' },
              { key: 'originalTotalAmount', label: 'Xidmətlərin Real Cəmi' },
              { key: 'totalAmount', label: 'Məbləğ' },
              { key: 'status', label: 'Status' },
              { key: 'contractDate', label: 'Tarix' }
            ]}
            setIsExport={setIsExport}
          />
          <button
            onClick={handleOpenServiceTypeModal}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Xidmət Növləri
          </button>
          <div className="relative inline-block text-left">
            {canAddContract && (
              <button
                onClick={() => handleAdd()}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                Yeni Müqavilə
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <DocumentTextIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-blue-100 text-sm font-medium">Ümumi Müqavilələr</p>
              <p className="text-2xl font-bold">{totalContracts}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-green-100 text-sm font-medium">Aktiv Müqavilələr</p>
              <p className="text-2xl font-bold">{activeContracts}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <DocumentCheckIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-purple-100 text-sm font-medium">Tamamlanmış</p>
              <p className="text-2xl font-bold">{completedContracts}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BanknotesIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-orange-100 text-sm font-medium">Ümumi Gəlir</p>
              <p className="text-2xl font-bold">{totalRevenue.toLocaleString('az-AZ', { 
                minimumFractionDigits: 2, 
                maximumFractionDigits: 2 
              })} AZN</p>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Alerts */}
      {(overduePayments.length > 0 || upcomingPayments.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Overdue Payments */}
          {overduePayments.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
                <h3 className="text-sm font-medium text-red-800">
                  Gecikmiş Ödənişlər ({overduePayments.length})
                </h3>
              </div>
              <div className="space-y-2">
                {overduePayments.slice(0, 3).map((payment, index) => (
                  <div key={index} className="text-sm text-red-700">
                    <span className="font-medium">
                      {typeof payment.studentName === 'string' 
                        ? payment.studentName
                        : payment.studentName || 'Tələbə'}
                    </span> - 
                    {payment.amount} AZN ({payment.daysOverdue} gün gecikib)
                  </div>
                ))}
                {overduePayments.length > 3 && (
                  <div className="text-xs text-red-600">
                    və {overduePayments.length - 3} digər...
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Upcoming Payments */}
          {upcomingPayments.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <ClockIcon className="h-5 w-5 text-yellow-600" />
                <h3 className="text-sm font-medium text-yellow-800">
                  Yaxınlaşan Ödənişlər ({upcomingPayments.length})
                </h3>
              </div>
              <div className="space-y-2">
                {upcomingPayments.slice(0, 3).map((payment, index) => (
                  <div key={index} className="text-sm text-yellow-700">
                    <span className="font-medium">
                      {typeof payment.studentName === 'string' 
                        ? payment.studentName
                        : payment.studentName || 'Tələbə'}
                    </span> - 
                    {payment.amount} AZN ({payment.daysUntilDue} gün qalıb)
                  </div>
                ))}
                {upcomingPayments.length > 3 && (
                  <div className="text-xs text-yellow-600">
                    və {upcomingPayments.length - 3} digər...
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recent Student Applications for Contract Creation */}
      {false && (
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <DocumentTextIcon className="h-5 w-5 text-blue-600 mr-2" />
            Müqavilə Yaratmaq Üçün Hazır Müraciətlər
          </h3>
          <span className="text-sm text-gray-500">
            Qəbul almış tələbələr ({students.filter(student => (student.statusId || student.status_id) === 6 && (student.isActive || student.is_active)).length})
          </span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {students
            .filter(student => (student.statusId || student.status_id) === 6 && (student.isActive || student.is_active)) // Qəbul almış tələbələr
            .slice(0, 6) // Show only first 6
            .map(student => {
              const application = {
                universityId: student.universityId || 1,
                programId: student.programId || 1,
                university: student.university,
                program: student.program,
                status: student.status
              };
              
              return (
                <div key={student.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all duration-200">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">
                        {typeof (student.firstName || student.first_name) === 'object' 
                          ? (student.firstName || student.first_name)?.name || 'Ad'
                          : (student.firstName || student.first_name) || 'Ad'} {' '}
                        {typeof (student.lastName || student.last_name) === 'object' 
                          ? (student.lastName || student.last_name)?.name || 'Soyad'
                          : (student.lastName || student.last_name) || 'Soyad'}
                      </h4>
                      <p className="text-xs text-gray-500">
                        {typeof student.consultant === 'object' 
                          ? student.consultant?.name || 'Konsultant'
                          : student.consultant || 'Konsultant'}
                      </p>
                    </div>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                      Qəbul alıb
                    </span>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="text-xs text-gray-600">
                      <span className="font-medium">Universitet:</span> {' '}
                      {student.university?.name || 'Universitet məlumatı yoxdur'}
                    </div>
                    <div className="text-xs text-gray-600">
                      <span className="font-medium">Proqram:</span> {' '}
                      {student.program?.name || 'Proqram məlumatı yoxdur'}
                    </div>
                    <div className="text-xs text-gray-600">
                      <span className="font-medium">Müraciət tarixi:</span> {' '}
                      {student.applicationDate ? new Date(student.applicationDate).toLocaleDateString('az-AZ') : 
                       student.firstContactDate ? new Date(student.firstContactDate).toLocaleDateString('az-AZ') : 'Tarix yoxdur'}
                    </div>
                  </div>
                  
                  {canAddContract && (
                    <button
                      onClick={() => handleAdd({
                        studentId: student.id,
                        universityId: application.universityId,
                        programId: application.programId,
                        applicationId: student.applicationId,
                        applicationDate: student.applicationDate || student.firstContactDate,
                        studentName: `${student.firstName || student.first_name || 'Ad'} ${student.lastName || student.last_name || 'Soyad'}`
                      })}
                      className="w-full inline-flex items-center justify-center px-3 py-2 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                    >
                      <PlusIcon className="h-3 w-3 mr-1" />
                      Müqavilə Yarat
                    </button>
                  )}
                </div>
              );
            })}
        </div>
        
        {students.filter(student => (student.statusId || student.status_id) === 6 && (student.isActive || student.is_active)).length > 6 && (
          <div className="mt-4 text-center">
            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Hamısını gör ({students.filter(student => (student.statusId || student.status_id) === 6 && (student.isActive || student.is_active)).length} müraciət)
            </button>
          </div>
        )}
        
        {students.filter(student => (student.statusId || student.status_id) === 6 && (student.isActive || student.is_active)).length === 0 && (
          <div className="text-center py-8">
            <div className="mx-auto h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <DocumentTextIcon className="h-6 w-6 text-gray-400" />
            </div>
            <p className="text-sm text-gray-500">
              Hazırda müqavilə yaratmaq üçün hazır müraciət yoxdur
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Müqavilə yaratmaq üçün tələbələrin statusu "Qəbul alıb" olmalıdır
            </p>
          </div>
        )}
      </div>
      )}

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Müqavilə №, tələbə, universitet..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Bütün statuslar</option>
            {contractStatusesAvailable.map(status => (
              <option key={status.id} value={status.id}>{status.name}</option>
            ))}
          </select>

          <select
            value={paymentFilter}
            onChange={(e) => setPaymentFilter(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Ödəniş statusu</option>
            {paymentStatusesAvailable.map(status => (
              <option key={status.id} value={status.id}>{status.name}</option>
            ))}
          </select>

          <select
            value={consultantFilter}
            onChange={(e) => setConsultantFilter(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Bütün konsultantlar</option>
            {consultants.filter(c => c.isActive || c.is_active).map(consultant => (
              <option key={consultant.id} value={consultant.id}>
                {typeof consultant.name === 'object' 
                  ? consultant.name?.name || 'Konsultant'
                  : consultant.name || 'Konsultant'}
              </option>
            ))}
          </select>

          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('');
              setPaymentFilter('');
              setConsultantFilter('');
              setCurrentPage(1);
            }}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Təmizlə
          </button>
        </div>
      </div>

      {/* Contracts table */}
      <div className="bg-white shadow-lg overflow-hidden sm:rounded-xl border border-blue-100">
        <div className="px-6 py-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-blue-50 to-blue-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Müqavilə
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Tələbə
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Universitet
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Məbləğ
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Status
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Əməliyyatlar
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {paginatedContracts.map((contract) => {
                  const attentionLevel = getContractAttentionLevel(contract);
                  const rowClass = attentionLevel === 'overdue' 
                    ? 'hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100 bg-red-25' 
                    : attentionLevel === 'warning'
                    ? 'hover:bg-gradient-to-r hover:from-yellow-50 hover:to-yellow-100 bg-yellow-25'
                    : 'hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100';
                  
                  return (
                    <tr key={contract.id} className={`${rowClass} transition-all duration-200 group`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow mr-3">
                            <DocumentTextIcon className="h-5 w-5 text-white" />
                            {attentionLevel === 'overdue' && (
                              <div className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full border border-white"></div>
                            )}
                            {attentionLevel === 'warning' && (
                              <div className="absolute -top-1 -right-1 h-3 w-3 bg-yellow-500 rounded-full border border-white"></div>
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-900 group-hover:text-blue-900 transition-colors">
                              {contract.contract_number || contract.contractNumber || 'Naməlum'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {contract.contract_date ? new Date(contract.contract_date).toLocaleDateString('az-AZ') : 
                               contract.contractDate ? new Date(contract.contractDate).toLocaleDateString('az-AZ') : 'Naməlum'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="font-medium">
                            {contract.student 
                              ? `${contract.student.first_name || ''} ${contract.student.last_name || ''}`.trim()
                              : (contract.studentName || contract.student_name || 'Naməlum')
                            }
                          </div>
                          <div className="text-xs text-gray-500">
                            {contract.consultant?.admin 
                              ? `${contract.consultant.admin.first_name || ''} ${contract.consultant.admin.last_name || ''}`.trim()
                              : (contract.consultantName || contract.consultant_name || 'Naməlum')
                            }
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="font-medium">
                            {contract.university?.name || contract.universityName || contract.university_name || 'Naməlum'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {contract.program?.name || contract.programName || contract.program_name || 'Proqram təyin edilməyib'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="font-semibold">
                            {contract.totalAmount || contract.total_amount} {contract.currency || 'AZN'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {mapPaymentTypeToUI(contract.payment_type || contract.paymentType)} ödəniş
                          </div>
                          {(contract.original_total_amount && contract.original_total_amount !== (contract.totalAmount || contract.total_amount)) && (
                            <div className="text-xs text-gray-400">
                              Əsl: {contract.original_total_amount} AZN
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          {getStatusBadge(contract.status_id || contract.statusId, contract.status?.name || contract.status)}
                          {getPaymentStatusBadge(contract)}
                          {attentionLevel === 'overdue' && (
                            <div className="text-xs text-red-600 font-medium bg-red-50 px-2 py-1 rounded border border-red-200">
                              ⚠️ Gecikmiş ödəniş
                            </div>
                          )}
                          {attentionLevel === 'warning' && (
                            <div className="text-xs text-yellow-600 font-medium bg-yellow-50 px-2 py-1 rounded border border-yellow-200">
                              ⚠️ Ödəniş gözlənilir
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex justify-end space-x-2">
                          {canViewContract && (
                            <button
                              onClick={() => handleView(contract)}
                              className="inline-flex items-center p-2 bg-green-100 text-green-600 hover:bg-green-200 hover:text-green-800 rounded-lg transition-all duration-200 hover:scale-105"
                              title="Ətraflı bax"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                          )}
                          {canEditContract && (
                            (() => {
                              const hasPaidPayments = hasAnyPaidPayments(contract);
                              return (
                                <button
                                  onClick={hasPaidPayments ? undefined : () => handleEdit(contract)}
                                  disabled={hasPaidPayments}
                                  className={`inline-flex items-center p-2 rounded-lg transition-all duration-200 ${
                                    hasPaidPayments 
                                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                      : 'bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-800 hover:scale-105'
                                  }`}
                                  title={hasPaidPayments ? "Bu müqavilədə ödəniş yazılıb, redaktə etmək mümkün deyil" : "Redaktə et"}
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                              );
                            })()
                          )}
                          {canDeleteContract && (
                            <button
                              onClick={() => handleDeleteContract(contract)}
                              className="inline-flex items-center p-2 bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-800 rounded-lg transition-all duration-200 hover:scale-105"
                              title="Sil"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredContracts.length === 0 && (
            <div className="text-center py-16">
              <div className="mx-auto h-16 w-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mb-4">
                <DocumentTextIcon className="h-8 w-8 text-blue-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Heç bir müqavilə tapılmadı</h3>
              <p className="text-sm text-gray-500 max-w-sm mx-auto">
                Axtarış şərtlərinizi dəyişin və ya yeni müqavilə əlavə edin.
              </p>
              {canAddContract && (
                <button
                  onClick={() => handleAdd()}
                  className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  İlk Müqaviləni Əlavə Et
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalItems={filteredContracts.length}
        itemsPerPage={itemsPerPage}
      />

      {/* Continue with modals... */}

      {/* Add/Edit Contract Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingContract ? 'Müqaviləni Redaktə Et' : 'Yeni Müqavilə Yarat'}
                </h3>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setSelectedStudentApplication(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Show application context if creating from accepted application */}
              {selectedStudentApplication && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-600" />
                    <h4 className="text-sm font-medium text-green-900">Qəbul edilmiş müraciət əsasında müqavilə</h4>
                  </div>
                  <div className="text-sm text-green-700 space-y-1">
                    <p>
                      <span className="font-medium">Tələbə:</span> {selectedStudentApplication.studentName}
                    </p>
                    <p>
                      <span className="font-medium">Müraciət tarixi:</span> {' '}
                      {selectedStudentApplication.applicationDate 
                        ? new Date(selectedStudentApplication.applicationDate).toLocaleDateString('az-AZ')
                        : 'Təyin edilməyib'}
                    </p>
                    {selectedStudentApplication.applicationId && (
                      <p>
                        <span className="font-medium">Müraciət №:</span> {selectedStudentApplication.applicationId}
                      </p>
                    )}
                    <div className="mt-2 p-2 bg-green-100 rounded border border-green-300">
                      <p className="text-xs font-medium text-green-800">
                        ✓ Bu müqavilə qəbul edilmiş bir müraciət əsasında yaradılır
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Show application info if application is selected manually */}
              {!editingContract && !selectedStudentApplication && formData.application_id && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                    <h4 className="text-sm font-medium text-blue-900">Seçilmiş müraciət əsasında</h4>
                  </div>
                  <div className="text-sm text-blue-700">
                    <p>
                      <span className="font-medium">Müraciət №:</span> {formData.application_id}
                    </p>
                    {formData.student_id && (
                      <p>
                        <span className="font-medium">Tələbə:</span> {
                          students.find(s => s.id.toString() === formData.student_id)?.firstName ||
                          students.find(s => s.id.toString() === formData.student_id)?.first_name
                        } {
                          students.find(s => s.id.toString() === formData.student_id)?.lastName ||
                          students.find(s => s.id.toString() === formData.student_id)?.last_name
                        }
                      </p>
                    )}
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Student Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tələbə *
                      <span className="text-xs text-gray-500 ml-2">
                        (Qəbul almış tələbələr yaşıl rəngdə göstərilir)
                      </span>
                    </label>
                    <select
                      value={formData.student_id}
                      onChange={(e) => {
                        const student_id = e.target.value;
                        const student = students.find(s => s.id.toString() === student_id);
                        const consultant_id = student ? (student.consultantId || student.consultant_id || '').toString() : '';
                        setFormData({
                          ...formData,
                          student_id,
                          application_id: '', // Reset application when student changes
                          consultant_id,
                          university_id: '',
                          program_id: ''
                        });
                      }}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Tələbə seçin</option>
                      {students.filter(s => s.isActive || s.is_active).map(student => {
                        const isAccepted = (student.statusId || student.status_id) === 6;
                        return (
                          <option 
                            key={student.id} 
                            value={student.id}
                            style={isAccepted ? {color: '#059669', fontWeight: 'bold'} : {}}
                          >
                            {typeof (student.firstName || student.first_name) === 'object' 
                              ? (student.firstName || student.first_name)?.name || 'Ad'
                              : (student.firstName || student.first_name) || 'Ad'} {' '}
                            {typeof (student.lastName || student.last_name) === 'object' 
                              ? (student.lastName || student.last_name)?.name || 'Soyad'
                              : (student.lastName || student.last_name) || 'Soyad'}
                            {isAccepted && ' ✓'}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {/* Application Selection - Show only if student is selected */}
                  {formData.student_id && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Müraciət
                        <span className="text-xs text-gray-500 ml-2">
                          (İstəyə bağlı - müəyyən müraciət üçün müqavilə yaratmaq istəyirsinizsə seçin)
                        </span>
                      </label>
                      <select
                        value={formData.application_id}
                        onChange={(e) => {
                          const application_id = e.target.value;
                          let university_id = '';
                          let program_id = '';
                          
                          // If an application is selected, auto-fill university and program
                          if (application_id && applicationsData?.data?.data) {
                            const selectedApplication = applicationsData.data.data.find(app => 
                              app.id.toString() === application_id
                            );
                            if (selectedApplication) {
                              university_id = selectedApplication.university_id?.toString() || '';
                              program_id = selectedApplication.program_id?.toString() || '';
                            }
                          }
                          
                          setFormData({
                            ...formData,
                            application_id,
                            university_id,
                            program_id
                          });
                        }}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Müraciət seçin (istəyə bağlı)</option>
                        {(() => {
                          if (!applicationsData?.data?.data || !formData.student_id) return null;
                          
                          // Get applications for the selected student
                          const studentApplications = applicationsData.data.data.filter(app => 
                            app.student && app.student.id.toString() === formData.student_id
                          );
                          
                          if (studentApplications.length === 0) {
                            return (
                              <option disabled>Bu tələbə üçün müraciət tapılmadı</option>
                            );
                          }
                          
                          return studentApplications.map(application => (
                            <option key={application.id} value={application.id}>
                              Müraciət #{application.id}
                              {application.university?.name && ` - ${application.university.name}`}
                              {application.program?.name && ` - ${application.program.name}`}
                              {application.application_date && ` (${new Date(application.application_date).toLocaleDateString('az-AZ')})`}
                              {application.status?.name && ` [${application.status.name}]`}
                            </option>
                          ));
                        })()}
                      </select>
                    </div>
                  )}

                  {/* Consultant (Auto-filled based on student) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Konsultant *
                    </label>
                    <select
                      value={formData.consultant_id}
                      onChange={(e) => setFormData({...formData, consultant_id: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Konsultant seçin</option>
                      {consultants.filter(c => c.isActive || c.is_active).map(consultant => (
                        <option key={consultant.id} value={consultant.id}>
                          {typeof consultant.name === 'object' 
                            ? consultant.name?.name || 'Konsultant'
                            : consultant.name || 'Konsultant'}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* University Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Universitet *
                    </label>
                    <select
                      value={formData.university_id}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          university_id: e.target.value,
                          program_id: '' // Reset program when university changes
                        });
                      }}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Universitet seçin</option>
                      {universities.filter(u => u.isActive || u.is_active).map(university => (
                        <option key={university.id} value={university.id}>
                          {typeof university.name === 'object' 
                            ? university.name?.name || 'Universitet'
                            : university.name || 'Universitet'}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Program Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Proqram *
                    </label>
                    <select
                      value={formData.program_id}
                      onChange={(e) => setFormData({...formData, program_id: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                      disabled={!formData.university_id}
                    >
                      <option value="">Proqram seçin</option>
                      {universityPrograms
                        .filter(p => {
                          const universityId = p.universityId || p.university_id;
                          const isActive = p.isActive || p.is_active;
                          return universityId.toString() === formData.university_id && isActive;
                        })
                        .map(program => (
                          <option key={program.id} value={program.id}>
                            {typeof program.name === 'object' 
                              ? program.name?.name || 'Proqram'
                              : program.name || 'Proqram'} ({getDegreeNames(program.degree) || 'Dərəcə'})
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Contract Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Müqavilə Tarixi *
                    </label>
                    <input
                      type="date"
                      value={formData.contract_date}
                      onChange={(e) => setFormData({...formData, contract_date: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  {/* Start Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Başlama Tarixi *
                    </label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bitmə Tarixi *
                    </label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  {/* Payment Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ödəniş Növü *
                    </label>
                    <select
                      value={formData.payment_type}
                      onChange={(e) => {
                        const paymentType = e.target.value;
                        setFormData({
                          ...formData, 
                          payment_type: paymentType,
                          payment_installments: paymentType === 'Hissəli' ? 2 : 1,
                          payment_dates: paymentType === 'Hissəli' ? ['', ''] : []
                        });
                      }}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="Tam">Tam Ödəniş</option>
                      <option value="Hissəli">Hissəli Ödəniş</option>
                    </select>
                  </div>

                  {/* Installment Configuration - Show only for Hissəli payment */}
                  {formData.payment_type === 'Hissəli' && (
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <h4 className="text-sm font-medium text-blue-900 mb-3">Hissəli Ödəniş Konfiqurasiyası</h4>
                      
                      {/* Number of installments */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Hissə Sayı *
                        </label>
                        <input
                          type="number"
                          min="2"
                          max="12"
                          value={formData.payment_installments}
                          onChange={(e) => {
                            const installments = parseInt(e.target.value) || 2;
                            const newDates = Array(installments).fill('').map((_, i) => 
                              formData.payment_dates[i] || ''
                            );
                            setFormData({
                              ...formData,
                              payment_installments: installments,
                              payment_dates: newDates
                            });
                          }}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Minimum 2, maksimum 12 hissə
                        </p>
                      </div>

                      {/* Payment dates table */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Hər Hissə üçün Ödəniş Tarixi
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Array.from({ length: formData.payment_installments }, (_, index) => {
                            const amount = formData.total_amount / formData.payment_installments;
                            const isLastPayment = index === formData.payment_installments - 1;
                            const calculatedAmount = isLastPayment 
                              ? formData.total_amount - (amount * (formData.payment_installments - 1))
                              : amount;
                            
                            return (
                              <div key={index} className="bg-white p-3 rounded border border-gray-200">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="text-sm font-medium text-gray-700">
                                    {index + 1}. Hissə
                                  </span>
                                  <span className="text-sm text-blue-600 font-medium">
                                    {calculatedAmount.toFixed(2)} AZN
                                  </span>
                                </div>
                                <input
                                  type="date"
                                  value={formData.payment_dates[index] || ''}
                                  onChange={(e) => {
                                    const newDates = [...formData.payment_dates];
                                    newDates[index] = e.target.value;
                                    setFormData({...formData, payment_dates: newDates});
                                  }}
                                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                                  required
                                />
                              </div>
                            );
                          })}
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                          Hər hissə üçün ödəniş tarixini təyin edin
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status *
                    </label>
                    <select
                      value={formData.status_id}
                      onChange={(e) => setFormData({...formData, status_id: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Status seçin</option>
                      {contractStatusesAvailable.map(status => (
                        <option key={status.id} value={status.id}>{status.name}</option>
                      ))}
                    </select>
                    {editingContract && (
                      <p className="mt-1 text-xs text-gray-500">
                        Cari status: {typeof editingContract.status === 'object' 
                          ? editingContract.status?.name || 'Status'
                          : editingContract.status || 'Status'}
                        {formData.status_id !== (editingContract.statusId || editingContract.status_id || '').toString() && (
                          <span className="text-orange-600 font-medium"> → Dəyişdirilir</span>
                        )}
                      </p>
                    )}
                  </div>
                </div>

                {/* Services Selection */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Xidmətlər *
                    </label>
                    <button 
                      type="button"
                      onClick={() => setIsServiceTypeModalOpen(true)}
                      className="text-xs text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Xidmətləri idarə et
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {serviceTypes.filter(service => (service.isActive !== false) && (service.is_active !== false)).map(service => (
                      <div key={service.id} className="flex items-center">
                        <input
                          type="checkbox"
                          id={`service-${service.id}`}
                          checked={formData.services.some(s => 
                            (s.service_id === service.id) || 
                            (s.service_type_id === service.id) ||
                            (s.id === service.id)
                          )}
                          onChange={(e) => {
                            console.log('Service checkbox change:', {
                              service,
                              checked: e.target.checked,
                              currentServices: formData.services
                            });
                            
                            if (e.target.checked) {
                              const newServices = [...formData.services, {
                                service_id: service.id,
                                service_name: service.name,
                                price: parseFloat(service.price) || 0,
                                currency: service.currency
                              }];
                              const originalTotal = newServices.reduce((sum, s) => sum + parseFloat(s.price), 0);
                              setFormData({
                                ...formData,
                                services: newServices,
                                original_total_amount: originalTotal,
                                discounted_amount: originalTotal, // Avtomatik eyni qiymət
                                total_amount: originalTotal
                              });
                            } else {
                              const newServices = formData.services.filter(s => 
                                s.service_id !== service.id && 
                                s.service_type_id !== service.id &&
                                s.id !== service.id
                              );
                              const originalTotal = newServices.reduce((sum, s) => sum + parseFloat(s.price), 0);
                              setFormData({
                                ...formData,
                                services: newServices,
                                original_total_amount: originalTotal,
                                discounted_amount: originalTotal, // Avtomatik eyni qiymət
                                total_amount: originalTotal
                              });
                            }
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor={`service-${service.id}`} className="ml-2 text-sm text-gray-700">
                          {typeof service.name === 'object' 
                            ? service.name?.name || 'Xidmət'
                            : service.name || 'Xidmət'} - {service.price} {service.currency}
                        </label>
                      </div>
                    ))}
                  </div>
                  {serviceTypes.filter(service => (service.isActive !== false) && (service.is_active !== false)).length === 0 && (
                    <div className="text-center py-4 text-sm text-gray-500">
                      Aktiv xidmət yoxdur. 
                      <a 
                        href="/contract-services" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline ml-1"
                      >
                        Xidmət əlavə edin
                      </a>
                    </div>
                  )}
                </div>

                {/* Pricing Information */}
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg border">
                  <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Xidmətlərin Real Cəmi:</span>
                      <span className="text-lg font-semibold text-gray-600">
                        {formData.original_total_amount} AZN
                      </span>
                    </div>
                  </div>
                  
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Endirimli Qiymət (Final Məbləğ) *
                    </label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max={formData.original_total_amount}
                        value={formData.discounted_amount}
                        onChange={(e) => {
                          const discountedAmount = parseFloat(e.target.value) || 0;
                          setFormData({
                            ...formData,
                            discounted_amount: discountedAmount,
                            total_amount: discountedAmount
                          });
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                        placeholder="0.00"
                        required
                      />
                      <span className="text-sm font-medium text-gray-700">AZN</span>
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      Bu məbləğ müqavilədə istifadə olunacaq və ödəniş cədvəlinə əsas götürüləcək
                    </p>
                    {formData.discounted_amount < formData.original_total_amount && (
                      <div className="mt-2 text-xs text-green-600 font-medium">
                        Endirimi: {(formData.original_total_amount - formData.discounted_amount).toFixed(2)} AZN 
                        ({(((formData.original_total_amount - formData.discounted_amount) / formData.original_total_amount) * 100).toFixed(1)}%)
                      </div>
                    )}
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Müqavilə Məbləği:</span>
                    <span className="text-lg font-bold text-blue-600">
                      {formData.total_amount} AZN
                    </span>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Qeydlər
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    rows={3}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Əlavə qeydlər..."
                  />
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Ləğv Et
                  </button>
                  <button
                    type="submit"
                    disabled={addLoading || updateLoading}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {(addLoading || updateLoading) ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {editingContract ? 'Yenilənir...' : 'Yaradılır...'}
                      </div>
                    ) : (
                      editingContract ? 'Yenilə' : 'Yarat'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Contract Modal */}
      {isViewModalOpen && viewingContract && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-5xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Müqavilə Məlumatları - {viewingContract.contract_number || viewingContract.contractNumber}
                </h3>
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Tab Navigation */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'details'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Əsas Məlumatlar
                  </button>
                  <button
                    onClick={() => setActiveTab('payments')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'payments'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Ödəniş Cədvəli
                  </button>
                  <button
                    onClick={() => setActiveTab('services')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'services'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Xidmətlər
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              {activeTab === 'details' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Müqavilə Nömrəsi</label>
                    <p className="mt-1 text-sm text-gray-900">{viewingContract.contract_number || viewingContract.contractNumber}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Tələbə</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {viewingContract.student ? 
                        `${viewingContract.student.first_name} ${viewingContract.student.last_name}` :
                        (viewingContract.studentName || 'Naməlum')
                      }
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Konsultant</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {viewingContract.consultant?.admin ? 
                        `${viewingContract.consultant.admin.first_name} ${viewingContract.consultant.admin.last_name}` :
                        (viewingContract.consultantName || 'Naməlum')
                      }
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Universitet</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {viewingContract.university?.name || viewingContract.universityName || 'Naməlum'}
                      {viewingContract.university?.country && (
                        <span className="text-gray-500"> - {viewingContract.university.country.name}</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Proqram</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {viewingContract.program?.name || viewingContract.programName || 'Naməlum'}
                    </p>
                  </div>
                  {viewingContract.original_total_amount && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Xidmətlərin Real Cəmi</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {viewingContract.original_total_amount} {viewingContract.currency || 'AZN'}
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Ümumi Məbləğ</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {viewingContract.total_amount || viewingContract.totalAmount} {viewingContract.currency || 'AZN'}
                      {viewingContract.original_total_amount && viewingContract.original_total_amount !== (viewingContract.total_amount || viewingContract.totalAmount) && (
                        <span className="text-xs text-green-600 ml-2">
                          (Endirimi: {(viewingContract.original_total_amount - (viewingContract.total_amount || viewingContract.totalAmount)).toFixed(2)} AZN)
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Ödəniş Növü</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {mapPaymentTypeToUI(viewingContract.payment_type || viewingContract.paymentType)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Status</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {viewingContract.status?.name || 
                       (typeof viewingContract.status === 'object' ? viewingContract.status?.name : viewingContract.status) || 
                       'Naməlum'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Müqavilə Tarixi</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {viewingContract.contract_date ? 
                        new Date(viewingContract.contract_date).toLocaleDateString('az-AZ') :
                        (viewingContract.contractDate || 'Naməlum')
                      }
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Başlama Tarixi</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {viewingContract.start_date ? 
                        new Date(viewingContract.start_date).toLocaleDateString('az-AZ') :
                        (viewingContract.startDate || 'Naməlum')
                      }
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Bitmə Tarixi</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {viewingContract.end_date ? 
                        new Date(viewingContract.end_date).toLocaleDateString('az-AZ') :
                        (viewingContract.endDate || 'Naməlum')
                      }
                    </p>
                  </div>
                  {viewingContract.application && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Müraciət ID</label>
                      <p className="mt-1 text-sm text-gray-900">#{viewingContract.application.id}</p>
                    </div>
                  )}
                  {viewingContract.notes && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-500">Qeydlər</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingContract.notes}</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'payments' && (
                <div>
                  <div className="mb-4 flex justify-between items-center">
                    <h4 className="text-lg font-medium text-gray-900">Ödəniş Cədvəli</h4>
                    <div className="text-sm text-gray-500">
                      Ümumi: {viewingContract.total_amount || viewingContract.totalAmount} {viewingContract.currency || 'AZN'}
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Məbləğ
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Son Tarix
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Ödənilən
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {viewingContract.payment_schedule && viewingContract.payment_schedule.length > 0 ? (
                          viewingContract.payment_schedule.map((payment, index) => (
                            <tr key={index}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {payment.amount} {payment.currency}
                                <div className="text-xs text-gray-500">{payment.description}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {payment.due_date ? 
                                  new Date(payment.due_date).toLocaleDateString('az-AZ') :
                                  (payment.dueDate || 'Naməlum')
                                }
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {getStatusBadge(payment.status_id || payment.statusId, payment.status?.name || payment.status)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {payment.paid_amount || payment.paidAmount || '0.00'} {payment.currency}
                                {(payment.paid_date || payment.paidDate) && (
                                  <div className="text-xs text-gray-500">
                                    {payment.paid_date ? 
                                      new Date(payment.paid_date).toLocaleDateString('az-AZ') :
                                      payment.paidDate
                                    }
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                              <p>Bu müqavilə üçün ödəniş cədvəli tapılmadı</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'services' && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Xidmətlər</h4>
                  <div className="space-y-3">
                    {viewingContract.services && viewingContract.services.length > 0 ? (
                      viewingContract.services.map((service, index) => (
                        <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <div>
                            <span className="text-sm font-medium text-gray-900">
                              {service.service_type?.name || service.serviceName || 'Xidmət'}
                            </span>
                            {service.service_type?.description && (
                              <p className="text-xs text-gray-500 mt-1">{service.service_type.description}</p>
                            )}
                            <p className="text-xs text-gray-500">
                              Miqdar: {service.quantity || 1} ədəd
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="text-sm text-gray-600">
                              {service.price || service.total_amount} {service.currency || 'AZN'}
                            </span>
                            {service.quantity > 1 && (
                              <p className="text-xs text-gray-500">
                                ({service.quantity} × {(parseFloat(service.price) / service.quantity).toFixed(2)})
                              </p>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <p>Bu müqavilə üçün heç bir xidmət tapılmadı</p>
                      </div>
                    )}
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg border-t-2 border-blue-200 mt-4">
                      <span className="text-sm font-semibold text-gray-900">Ümumi</span>
                      <span className="text-sm font-semibold text-blue-600">
                        {viewingContract.total_amount || viewingContract.totalAmount} {viewingContract.currency || 'AZN'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Modal Actions */}
              <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Bağla
                </button>
                {canEditContract && (
                  <button
                    onClick={() => {
                      setIsViewModalOpen(false);
                      handleEdit(viewingContract);
                    }}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Redaktə Et
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {isPaymentModalOpen && selectedPayment && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Ödəniş Et
                </h3>
                <button
                  onClick={() => setIsPaymentModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handlePaymentSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ödəniləcək Məbləğ *
                  </label>
                  <input
                    type="number"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                    min="0"
                    max={selectedPayment.amount - (selectedPayment.paid_amount || selectedPayment.paidAmount)}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Maksimum: {selectedPayment.amount - (selectedPayment.paid_amount || selectedPayment.paidAmount)} AZN
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ödəniş Üsulu *
                  </label>
                  <select
                    value={paymentData.paymentMethod}
                    onChange={(e) => setPaymentData({...paymentData, paymentMethod: e.target.value})}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="Bank köçürməsi">Bank köçürməsi</option>
                    <option value="Nəğd">Nəğd</option>
                    <option value="Kart">Kart</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Referans
                  </label>
                  <input
                    type="text"
                    value={paymentData.reference}
                    onChange={(e) => setPaymentData({...paymentData, reference: e.target.value})}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ödəniş referansı..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Qeydlər
                  </label>
                  <textarea
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
                    rows={3}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Əlavə qeydlər..."
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setIsPaymentModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Ləğv Et
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                  >
                    Ödənişi Qeydə Al
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Service Types Modal */}
      {isServiceTypeModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Xidmət Növləri</h2>
              <button
                onClick={handleCloseServiceTypeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Add Service Type Form */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingServiceType ? 'Xidmət Növünü Redaktə Et' : 'Yeni Xidmət Növü'}
              </h3>
              <form onSubmit={handleServiceTypeSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Xidmət Adı <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={serviceTypeFormData.name}
                      onChange={(e) => setServiceTypeFormData({...serviceTypeFormData, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Xidmət adını daxil edin"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Təsvir
                    </label>
                    <input
                      type="text"
                      value={serviceTypeFormData.description}
                      onChange={(e) => setServiceTypeFormData({...serviceTypeFormData, description: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Təsvir daxil edin"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Qiymət <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={serviceTypeFormData.price}
                      onChange={(e) => setServiceTypeFormData({...serviceTypeFormData, price: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Qiymət daxil edin"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valyuta <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={serviceTypeFormData.currency}
                      onChange={(e) => setServiceTypeFormData({...serviceTypeFormData, currency: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="AZN">AZN</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <div className="flex items-center mt-2">
                      <input
                        type="checkbox"
                        checked={serviceTypeFormData.is_active}
                        onChange={(e) => setServiceTypeFormData({...serviceTypeFormData, is_active: e.target.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {serviceTypeFormData.is_active ? 'Aktiv' : 'Deaktiv'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCloseServiceTypeModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Ləğv et
                  </button>
                  <button
                    type="submit"
                    disabled={addServiceTypeLoading || updateServiceTypeLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {addServiceTypeLoading || updateServiceTypeLoading ? 'Yüklənir...' : (editingServiceType ? 'Yenilə' : 'Əlavə et')}
                  </button>
                </div>
              </form>
            </div>

            {/* Service Types Table */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Mövcud Xidmət Növləri</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Xidmət Adı
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Təsvir
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Qiymət
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Valyuta
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Əməliyyatlar
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {allServiceTypesLoading ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center">
                            <Audio height="40" width="40" radius="9" color="blue" ariaLabel="loading" />
                          </div>
                        </td>
                      </tr>
                    ) : allServiceTypesData && allServiceTypesData.data ? (
                      allServiceTypesData.data.map((serviceType) => (
                        <tr key={serviceType.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{serviceType.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{serviceType.description}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{serviceType.price}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{serviceType.currency}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              serviceType.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {serviceType.is_active ? 'Aktiv' : 'Deaktiv'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={() => handleEditServiceType(serviceType)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => handleDeleteServiceType(serviceType.id)}
                                className="text-red-600 hover:text-red-900"
                                disabled={deleteServiceTypeLoading}
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                          Heç bir xidmət növü tapılmadı
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 